import { Stack } from '@mui/material';
import { usePatchAdminLaboratoriesSubscriptionPlanApi } from 'api/admin/laboratories/subscription_plan';
import Modal, { ModalState } from 'components/shared/modal';
import { RhfNumberField, RhfSelectField } from 'components/shared/form/rhf_input';
import ThemedButton from 'components/shared/themed_button';
import { GlobalStateContext } from 'contexts/global_state_context';
import {
  subscriptionLimitCount,
  SubscriptionPlan,
  SubscriptionPlanForm,
  subscriptionNameItems,
} from 'entities/subscription_plan';
import React, { ChangeEvent, useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useEffectSkipFirst } from 'utils/hooks/use_effect_skip_first';

const PLANS_WITH_EDITABLE_LIMIT = ['free', 'standard_extension'];

type SubscriptionPlanModalProps = {
  modalState: ModalState;
  laboratoryId: number;
  subscriptionPlan: SubscriptionPlan;
  currentInstrumentCount: number;
  onComplete?: () => void;
};

export const SubscriptionPlanModal: React.FC<SubscriptionPlanModalProps> = (props) => {
  const globalState = useContext(GlobalStateContext);
  const patchPlanApi = usePatchAdminLaboratoriesSubscriptionPlanApi(props.laboratoryId);

  const form = useForm<SubscriptionPlanForm>({
    defaultValues: {
      name: 'volume',
      limitCount: '',
    },
  });
  const watchName = form.watch('name');

  useEffect(() => {
    if (!props.modalState.open) return;

    form.reset({
      name: props.subscriptionPlan.name,
      limitCount: props.subscriptionPlan.limitCount?.toString() || '',
    });
  }, [props.subscriptionPlan, props.modalState.open]);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const selectedName = e.target.value;
    const limitCount = subscriptionLimitCount[selectedName];
    form.setValue('name', selectedName);
    form.setValue('limitCount', limitCount);
  };

  const handleSubmit = () => {
    patchPlanApi.mutate(form.getValues(), {
      onSuccess: () => {
        globalState.setNotificationMessage({ body: 'プランを変更しました' });
        props.onComplete && props.onComplete();
      },
    });
  };

  const handleClose = () => {
    props.modalState.setOpen(false);
  };

  useEffectSkipFirst(() => {
    globalState.setLoading(patchPlanApi.isLoading);
  }, [patchPlanApi.isLoading]);

  return (
    <Modal
      modalId="subscription-plan-modal"
      title="プラン変更"
      modalState={props.modalState}
      size="md"
      footer={
        <>
          <ThemedButton onClick={handleClose} color="secondary">
            キャンセル
          </ThemedButton>
          <ThemedButton onClick={handleSubmit} color="success">
            保存
          </ThemedButton>
        </>
      }
    >
      <Stack gap={2}>
        <RhfSelectField
          control={form.control}
          label="プラン名"
          name="name"
          onChange={handleNameChange}
          items={subscriptionNameItems}
        />
        <Stack gap={1}>
          <RhfNumberField
            control={form.control}
            label="登録可能な機器数"
            name="limitCount"
            disabled={!watchName || !PLANS_WITH_EDITABLE_LIMIT.includes(watchName)}
          />
          {watchName === 'volume' && <p>無制限に登録できます</p>}
        </Stack>
      </Stack>
    </Modal>
  );
};
